import { AxiosResponse } from 'axios'
import lodash from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'

import useAlert from '../../../../1stlog-ui/src/components/Alert/useAlert'
import FinalForm from '../../../../1stlog-ui/src/components/Form'
import LoaderOverlay from '../../../../1stlog-ui/src/components/LoaderOverlay'
import { usePrevious } from '../../../../1stlog-ui/src/hooks'
import { IBaseAddress } from '../../../../1stlog-ui/src/types'
import { request } from '../../../../1stlog-ui/src/utils'
import { TOrderEntryColiService } from '../../../../shipmetrics-shared/containers/OrderEntry/AdditionalServices/types'
import defaultSettings from '../../../../shipmetrics-shared/containers/OrderEntry/defaultSettings'
import { OrderEntryContextProvider } from '../../../../shipmetrics-shared/containers/OrderEntry/OrderEntryContext'
import {
  TOrderEntryFormValues,
  TOrderEntryProduct,
} from '../../../../shipmetrics-shared/containers/OrderEntry/types'
import {
  fixInitialValues,
  fixOrderEntrySettings,
  getInitialValuesFromSettings,
  getValuesFromExternal,
  mergeAdditionalValues,
} from '../../../../shipmetrics-shared/containers/OrderEntry/utils'
import { TResult, TReturnAppReceiver } from '../TReturnApp'
import Form from './Form'

type TProps = {
  receiver: TReturnAppReceiver
  onResult: (result: TResult) => void
}

type TFormValues = {
  barcode: string
  colis: Array<object>
  activatedBarcode: string
}

type TSubmitValues = {
  colis: Array<object>
  barcode: string
  address: IBaseAddress
  sendLabelToEmail?: string
  reason?: string
  reasonDescription?: string
  valueOfGoods?: {
    currency: string
    value: number
  }
  packaging?: string
}

function stringifyValues(values): TSubmitValues {
  const {
    barcode,
    pickup: { address },
    colis,
    sendLabelToEmail,
    reason,
    reasonDescription,
    valueOfGoods,
    packaging,
  } = values

  return {
    barcode,
    address,
    colis,
    sendLabelToEmail,
    reason,
    reasonDescription,
    valueOfGoods,
    packaging,
  }
}

const ReturnForm = (props: TProps): React.ReactElement => {
  const { receiver, onResult } = props

  // initial form values
  const [initialValues, setInitialValues] = useState<TOrderEntryFormValues>()
  const [isLoading, setIsLoading] = useState(false)
  const { alert, setMessage: setError } = useAlert({
    danger: true,
    title: i18n.t('order_entry_failed', 'Erfassen fehlgeschlagen'),
  })

  const requestSubmit = async (values: TSubmitValues): Promise<void> => {
    try {
      setIsLoading(true)
      const { data, status } = (await request({
        method: 'post',
        url: '/return/submit',
        data: { short: receiver.shortcode, values },
      })) as AxiosResponse<any>
      onResult({
        label: data,
        nolabel: Boolean(values.barcode) && values.barcode !== '',
      })
    } catch (err) {
      // display error alert
      // TODO: check which error. if 400, display form validation errors
      setError(
        `${i18n.t(
          'error_submit_return_order',
          'Fehler beim Erfassen der Retoure. Bitte versuchen Sie es später erneut.',
        )}
        (${lodash.get(
          err,
          'response.data.error.desc',
          i18n.t('unknown_error', 'Unbekannter Fehler'),
        )})`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (values: TFormValues): Promise<object | void> => {
    const { barcode, activatedBarcode } = values
    if (barcode && barcode !== '' && barcode !== activatedBarcode) {
      return {
        barcode: i18n.t(
          'error_label_activator_barcode_not_submitted',
          'Bitte suchen und validieren Sie zunächst den eingegebenen Barcode',
        ),
      }
    }
    await requestSubmit(stringifyValues(values))
    return
  }

  // actually used for OrderEntryContext
  const settings = useMemo(() => {
    const coliServices: TOrderEntryColiService[] = []
    if (receiver?.showHazmatForm) {
      coliServices.push({
        label: 'Gefahrgutservice',
        level: 'coli',
        name: 'GGS',
        values: [{ type: 'hazmat', label: 'Gefahrgut' }],
      })
    }
    const allowedPackagingList = !lodash.isEmpty(receiver.limitToPackaging)
      ? receiver.packagingTypes.filter((p) =>
          receiver.limitToPackaging.includes(p.key),
        )
      : receiver.packagingTypes
    const product: TOrderEntryProduct = {
      isContentDescriptionAllowed: lodash.get(receiver, 'showContentFields'),
      isContentRequired: lodash.get(receiver, 'contentFieldsMandatory'),
      isMeasurementsHidden: !lodash.get(receiver, 'showMeasurementFields'),
      measurementsMandantory: lodash.get(
        receiver,
        'measurementFieldsMandatory',
      ),
      isWeightHidden: !lodash.get(receiver, 'showWeightFields'),
      isWeightOptional: !lodash.get(receiver, 'weightFieldsMandatory'),
      isColiReferenceTextHidden: !lodash.get(receiver, 'showRefFields'),
      isColiReferenceTextMandantory: lodash.get(receiver, 'refFieldsMandatory'),
      coliServices,
      isvalueOfGoodsAllowed: lodash.get(receiver, 'enable_value_of_goods'),
      isValueOfGoodsRequired: lodash.get(
        receiver,
        'valueOfGoodsMustBeSpecified',
      ),
      isPackagingAllowed: receiver.isPackagingAllowed,
      allowAnyPackaging: false,
      allowedPackagingList,
      defaultPackaging:
        lodash.size(allowedPackagingList) === 1
          ? lodash.first(allowedPackagingList)?.key
          : null,
    }
    return fixOrderEntrySettings({
      ...defaultSettings,
      id: 'ReturnAppUpdate',
      hazmatUrl: 'api/hazmat/search',
      barcodeUrl: 'api/validate',
      reusableBoxUrl: 'api/validateReusableBox',
      addressbookUrl: 'api/addressbook',
      customsUrl: 'api/customs',
      currentCustomer: {
        defaultCountry:
          receiver?.defaultPickUpCountry ??
          global?.tenantSettings?.defaultCountry,
      },
      preset: {
        defaultWeight: receiver?.weightFieldMustBeEntered
          ? null
          : lodash.toNumber(global.tenantSettings?.defaultWeight),
        defaultLength: global.tenantSettings?.defaultLength,
        defaultWidth: global.tenantSettings?.defaultWidth,
        defaultHeight: global.tenantSettings?.defaultHeight,
      },
      initialValues: initialValues as TOrderEntryFormValues,
      depots: [],
      isInternal: false,
      scrollToTop: () =>
        setTimeout(
          () => document?.getElementById('App/Content')?.scrollTo(0, 0),
          0,
        ),
      constantOrderEntryProduct: product,
      isAnonymous: true,
    })
  }, [initialValues, receiver])

  const setInitialOrderEntryValues = (
    extraValuesArray,
    { preset, currentCustomer },
  ): void => {
    let values = getInitialValuesFromSettings(settings)
    lodash.forEach(extraValuesArray, (extraValues) => {
      values = mergeAdditionalValues(values, getValuesFromExternal(extraValues))
    })
    values = fixInitialValues(values, { preset, currentCustomer })
    setInitialValues(values)
  }

  // build form values from settings and a possible array of external values
  useEffect(() => {
    const urlParamValues = undefined // TODO: read from url, use updateWithUrlParams()

    // const extraValues = [externalInitialValues]
    const extraValues = []

    // only use urlParamValues if templateValues are not set
    // if (!templateValues) {
    //   extraValues.push(urlParamValues)
    // } else {
    //   extraValues.push(templateValues)
    // }
    setInitialOrderEntryValues(extraValues.filter(Boolean), {
      preset: {},
      currentCustomer: undefined,
    })
  }, [])

  // re-mount form when initialValues change
  const [formKey, setFormKey] = useState<string>()
  const shuffleFormId = (): void => setFormKey(uuid())
  const previousInitialValues = usePrevious(initialValues)
  useEffect(() => {
    if (
      !initialValues ||
      (!previousInitialValues &&
        lodash.isEqual(previousInitialValues, initialValues))
    ) {
      return
    }
    shuffleFormId()
  }, [initialValues])

  if (!initialValues || !formKey) {
    return <></>
  }

  return (
    <>
      <LoaderOverlay isLoading={isLoading} fullscreen>
        {i18n.t('return_order_submit_loading', 'Retoure wird erfasst')}...
      </LoaderOverlay>
      <OrderEntryContextProvider value={settings} key={formKey}>
        <>
          <FinalForm
            initialValues={initialValues}
            showFormStateWindowCheckbox={/localhost/.test(window.location.href)}
            onSubmit={onSubmit}
          >
            <>
              <Form receiver={receiver} error={alert} settings={settings} />
            </>
          </FinalForm>
        </>
      </OrderEntryContextProvider>
    </>
  )
}

export default ReturnForm
